import type { ReactNode } from 'react';
import { Checkbox } from '../Checkbox/Checkbox';
import { Field } from '../Field/Field';
import styles from './CheckboxGroup.module.css';

/**
 * A labelled group of checkboxes over a set of ids — the held-certifications control of F26-b's
 * standard form (F26-b spec FR-U3, Q-16; F26-b WI-1.3 Decision 24), and the one F1-b's account form can reuse
 * for required certifications. In `src/ui` because one feature may not import another's component.
 *
 * Composition only: a `Field` in `labels="group"` mode (a `<fieldset>` named by its `<legend>`, the
 * error slot beneath and linked by `aria-describedby`) around one `Checkbox` per option.
 *
 * **A retired option** — a certification the record already holds but which is no longer active — is
 * listed with *(retired)* after its name, so the user can see why it is there and remove it.
 */
export interface CheckboxGroupOption {
    readonly id: string;
    /** Sentence case (§9). */
    readonly label: string;
    /** Marks the option *(retired)*. */
    readonly retired?: boolean;
}

export interface CheckboxGroupProps {
    readonly label: string;
    /** Forwarded to the group's `Field` — rendered in the `<legend>` after the label (`Field`'s `labelAddon`). */
    readonly labelAddon?: ReactNode;
    readonly options: readonly CheckboxGroupOption[];
    /** The checked ids. Controlled. */
    readonly values: readonly string[];
    /** The new set of checked ids, in option order. */
    readonly onChange: (values: readonly string[]) => void;
    readonly error?: string | undefined;
    readonly helper?: string;
    readonly disabled?: boolean;
    /** Fixes the group's field id. */
    readonly id?: string;
    /** The group's hook; each box takes `<id>-option-<option id>`. */
    readonly 'data-testid'?: string;
}

/** The option label as rendered — the name, then *(retired)* when it applies. */
export function checkboxGroupOptionLabel(option: CheckboxGroupOption): string {
    return option.retired === true ? `${option.label} (retired)` : option.label;
}

export function CheckboxGroup({
    label,
    labelAddon,
    options,
    values,
    onChange,
    error,
    helper,
    disabled = false,
    id,
    'data-testid': testId = 'checkbox-group',
}: CheckboxGroupProps) {
    const checked = new Set(values);

    function toggle(optionId: string, on: boolean) {
        onChange(
            options.map((option) => option.id).filter((each) => (each === optionId ? on : checked.has(each))),
        );
    }

    return (
        <Field
            data-testid={testId}
            error={error}
            label={label}
            labelAddon={labelAddon}
            labels="group"
            {...(helper === undefined ? {} : { helper })}
            {...(id === undefined ? {} : { id })}
        >
            <div className={styles.options}>
                {options.map((option) => (
                    <Checkbox
                        checked={checked.has(option.id)}
                        data-testid={`${testId}-option-${option.id}`}
                        disabled={disabled}
                        key={option.id}
                        label={checkboxGroupOptionLabel(option)}
                        onChange={(event) => toggle(option.id, event.target.checked)}
                    />
                ))}
            </div>
        </Field>
    );
}
